import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { DatePicker } from "@mui/x-date-pickers";
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SkillsInput from "../common/SkillsInput";
import { Radio, RadioGroup, FormControlLabel,Autocomplete,  Grid } from '@mui/material';
import { useContentPlayer } from '../../context/ContentPlayerContext';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
// import { SortableItem } from './SortableItem';

import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import {
  Upload,
  Close,
  Add,
  Visibility
} from '@mui/icons-material';
import { certificateService } from '../../services/api';
// import ContentSelectionModal from './ContentSelectionModal';
const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px'
});

const modules = {
  toolbar: {
    container: [
      [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      //['link', 'image', 'video'],
      ['clean']
    ]
  },
  clipboard: {
    matchVisual: false
  }
};
const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];


const CertificateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openContentPlayer } = useContentPlayer();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
  certification_name: '',
  description: '',
  certificate_status: '3',
  skills: [],
  auto_retire: null,
  days_to_complete: '',
  validity_months: '', 
  content_ordered: "0",
  });
  const [preview, setPreview] = useState('');

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          cover_picture: file
        }));
        setPreview(URL.createObjectURL(file));
      }
    },
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });

  useEffect(() => {
    if (id) {
      loadCourseData();
    }
  }, [id]);

  const loadCourseData = async () => {
    setLoading(true);

    try {
      const { data } = await certificateService.getCertificateById(id);
      setFormData({
        certification_name: data.record.name,
        description: data.record.description,
        certificate_status: data.record.status,
        auto_retire: data.record.auto_retire ? new Date(data.record.auto_retire) : null,
        days_to_complete:data.record.days_to_complete,
        validity_months: data.record.validity_months,
        skills: data.record.skills || [],
        content_ordered: data.record.content_ordered?.toString() || "0",
      });
      if (data.record.cover_picture) {
        setPreview(`/uploads/certificates/${data.record.cover_picture}`);
      }
    } catch (error) {
      <Alert severity="error">Error loading course</Alert>
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};
  //Validation
  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const newErrors = {};

    // Course name validation
    if (!formData.certification_name.trim()) {
      newErrors.certification_name = 'Certificate name is required';
    }
if (!stripHtml(formData.description).trim()) {
    newErrors.description = 'Certificate description is required';
  }

if (!formData.skills || formData.skills.length === 0) {
      newErrors.skills = 'Skills is required';
    }

    if (!formData.cover_picture && !preview) {
      newErrors.cover_picture = 'Course cover image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const response = id
        ? await certificateService.updateCertificate(id, formData)
        : await certificateService.createCertificate(formData);

      setSnackbar({
        open: true,
        message: 'Certificate saved successfully!',
        severity: 'success'
      });
      setTimeout(() => {
        navigate(`/certificates`);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Error saving certificate';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 7 }}
      >
        <MuiAlert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {id ? 'Edit Certificate' : 'Create New Certificate'}
      </Typography>


      <form onSubmit={handleSubmit}>
        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Certification Name"
            name="certification_name"
            value={formData.certification_name}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 3 }}
            error={!!errors.certification_name}
            helperText={errors.certification_name}
          />

        </FormSection>
        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Description
          </Typography>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(content) => setFormData(prev => ({
              ...prev,
              description: content
            }))}
            style={{ height: '200px', marginBottom: '50px' }}
            modules={modules}
            formats={formats}
          />
          {errors.description && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.description}
        </Typography>
      )}
        </FormSection>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Cover Image
        </Typography>
        <DropzoneArea {...getRootProps()}>
          <input {...getInputProps()} />
          {preview ? (
            <Box position="relative">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreview('');
                  setFormData(prev => ({ ...prev, cover_picture: null }));
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'white',
                  '&:hover': { backgroundColor: 'white' }
                }}
              >
                <Close />
              </IconButton>
              <PreviewImage src={preview} alt="Cover preview" />
            </Box>
          ) : (
            <Box>
              <Upload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag and drop your cover image
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Supported formats: Images [.jpeg, .jpg, .png]
              </Typography>
            </Box>
          )}
        </DropzoneArea>
        {errors.cover_picture && (
          <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
            {errors.cover_picture}
          </Typography>
        )}

        <FormSection>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Box>
              <Typography variant="h6" gutterBottom sx={{marginTop: "10px"}}>
                Sequencing of Courses
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Decide if learners must take modules in sequential order.
              </Typography>
            </Box>
            <RadioGroup
              row
              name="content_ordered"
              value={formData.content_ordered}
              onChange={handleChange}
            >
              <FormControlLabel
                value="0"
                control={<Radio />}
                label="Ordered"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
              />
              <FormControlLabel
                value="1"
                control={<Radio />}
                label="Unordered"
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
              />
            </RadioGroup>
          </Box>
        </FormSection>

        <FormSection>
  <DatePicker
    label="Auto Retire Date"
    value={formData.auto_retire}
    onChange={(newValue) =>
      setFormData(prev => ({
        ...prev,
        auto_retire: newValue,
      }))
    }
    renderInput={(params) => <TextField {...params} fullWidth />}
    minDate={new Date()}
  />
</FormSection>
 <FormSection>
  <TextField
    fullWidth
    type="number"
    name="validity_months"
    label="Validity (in months)"
    value={formData.validity_months || ''}
    onChange={(e) =>
      setFormData((prev) => ({
        ...prev,
        validity_months: parseInt(e.target.value, 10),
      }))
    }
    variant="outlined"
    InputProps={{ inputProps: { min: 1 } }}
  />
</FormSection>


  

<FormSection>
  <TextField
    fullWidth
    type="number"
    name="days_to_complete"
    label="Days to Complete"
    value={formData.days_to_complete || ''}
    onChange={handleChange}
    variant="outlined"
    InputProps={{ inputProps: { min: 0 } }}
  />
</FormSection>

<FormSection>
          <SkillsInput
            value={formData.skills}
            onChange={(newSkills) =>
              setFormData((prev) => ({
                ...prev,
                skills: newSkills,
              }))
            }
            error={!!errors.skills}
            helperText={errors.skills}
            required
          />
        </FormSection>

        <Box sx={{ mt: 3, textAlign: 'right' }}>
          <Button
            type="submit"
            variant="contained"
            // size="large"
            className="add-course-btn"
            // sx={{ px: 3, py: 1 }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CertificateForm;